import React, { useMemo } from 'react';
import {
  Box,
  SimpleGrid,
  Heading,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Icon,
  Flex,
  IconButton,
  Progress,
  Tooltip,
} from '@chakra-ui/react';
import { useCustomToast } from '../utils/customToast';
import {
  FaTrophy,
  FaMedal,
  FaAward,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaChartLine,
  FaStar,
  FaRegStar,
  FaFire,
} from 'react-icons/fa';
import { logger } from '../utils/logger';
import { useUser } from '../contexts/UserContext';

const LastMonthLeaders = ({ yearData, onBusinessClick }) => {
  const { toggleFavorite, isFavorited } = useUser();
  const toast = useCustomToast();

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const rankColor = useColorModeValue('gray.400', 'gray.500');

  // Use month -1 (October) since November data doesn't exist yet
  const lastMonthKey = useMemo(() => {
    // Always use month -1 (previous month from current date)
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const year = lastMonth.getFullYear();
    const month = String(lastMonth.getMonth() + 1).padStart(2, '0');
    const monthKey = `${year}-${month}`;

    logger.log(`Using month -1 (previous month): ${monthKey}`);
    return monthKey;
  }, []);

  // Calculate metrics for last month
  const lastMonthMetrics = useMemo(() => {
    if (!yearData || yearData.length === 0) {
      return {
        businesses: [],
        totalReviews: 0,
        marketGrowthRate: 0,
        comparisonMonth: null,
      };
    }

    const businesses = yearData
      .map((business) => {
        const monthlyData = business.data?.monthlyReviewCounts || {};
        const lastMonthReviews = monthlyData[lastMonthKey] || 0;

        // Calculate previous month for comparison
        const prevMonthDate = new Date(lastMonthKey + '-01T00:00:00');
        prevMonthDate.setMonth(prevMonthDate.getMonth() - 1);
        const prevMonthKey = `${prevMonthDate.getFullYear()}-${String(
          prevMonthDate.getMonth() + 1
        ).padStart(2, '0')}`;
        const prevMonthReviews = monthlyData[prevMonthKey] || 0;

        // Calculate total reviews up to last month (for base calculation)
        const allMonths = Object.keys(monthlyData).sort();
        const monthsBeforeLastMonth = allMonths.filter((m) => m < lastMonthKey);
        const baseReviews = monthsBeforeLastMonth.reduce(
          (sum, month) => sum + monthlyData[month],
          0
        );

        // Calculate relative growth percentage
        const relativeGrowth =
          baseReviews > 0 ? (lastMonthReviews / baseReviews) * 100 : 0;

        return {
          name: business.name,
          maps_uri: business.maps_uri,
          lastMonthReviews,
          prevMonthReviews,
          relativeGrowth,
          baseReviews,
          monthKey: lastMonthKey,
        };
      })
      .filter((b) => b.lastMonthReviews > 0); // Only include businesses with reviews last month

    // Calculate total market reviews for last month
    const totalReviews = businesses.reduce(
      (sum, b) => sum + b.lastMonthReviews,
      0
    );

    // Calculate market growth rate (comparison to previous month)
    const prevMonthDate = new Date(lastMonthKey + '-01T00:00:00');
    prevMonthDate.setMonth(prevMonthDate.getMonth() - 1);
    const prevMonthKey = `${prevMonthDate.getFullYear()}-${String(
      prevMonthDate.getMonth() + 1
    ).padStart(2, '0')}`;

    const totalPrevMonthReviews = businesses.reduce(
      (sum, b) => sum + b.prevMonthReviews,
      0
    );
    const marketGrowthRate =
      totalPrevMonthReviews > 0
        ? ((totalReviews - totalPrevMonthReviews) / totalPrevMonthReviews) * 100
        : 0;

    return {
      businesses,
      totalReviews,
      marketGrowthRate,
      comparisonMonth: prevMonthKey,
    };
  }, [yearData, lastMonthKey]);

  // Top 3 by absolute reviews
  const topByVolume = useMemo(() => {
    return [...lastMonthMetrics.businesses]
      .sort((a, b) => b.lastMonthReviews - a.lastMonthReviews)
      .slice(0, 3);
  }, [lastMonthMetrics.businesses]);

  // Top 3 by relative growth
  const topByGrowth = useMemo(() => {
    return [...lastMonthMetrics.businesses]
      .filter((b) => b.relativeGrowth > 0) // Only include positive growth
      .sort((a, b) => b.relativeGrowth - a.relativeGrowth)
      .slice(0, 3);
  }, [lastMonthMetrics.businesses]);

  // Format last month for display
  const lastMonthDisplay = useMemo(() => {
    // Parse the year-month string correctly to avoid timezone issues
    const [year, month] = lastMonthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [lastMonthKey]);

  // Handle business click
  const handleBusinessClick = (business) => {
    if (onBusinessClick && business) {
      onBusinessClick(business);
    }
  };

  // Handle favorite toggle (users can only have one favorite)
  const handleFavoriteClick = async (e, mapsUri) => {
    e.stopPropagation(); // Prevent triggering the business click
    try {
      const result = await toggleFavorite(mapsUri);
      if (result.isNowFavorited) {
        toast({
          title: 'Great Choice! 🎉',
          description: 'Your favorite will now be ⭐ starred on all dashboards',
          status: 'success',
          duration: 3500,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Favorite removed',
          status: 'info',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      logger.error('Error toggling favorite:', error);
      toast({
        title: 'Error',
        description: 'Failed to update favorite status.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // New aesthetic LeaderRow component
  const LeaderRow = ({
    business,
    position,
    metric,
    unit = '',
    colorScheme = 'blue',
    maxMetric,
  }) => {
    const isTop3 = position < 3;
    const rankIcons = [FaTrophy, FaMedal, FaAward];
    const rankColors = ['yellow.400', 'gray.400', 'orange.400'];

    const percentage = maxMetric > 0 ? (metric / maxMetric) * 100 : 0;

    return (
      <Flex
        p={3}
        bg={cardBg}
        borderRadius='lg'
        borderWidth='1px'
        borderColor={borderColor}
        align='center'
        mb={3}
        cursor='pointer'
        transition='all 0.2s'
        _hover={{
          transform: 'translateY(-2px)',
          shadow: 'md',
          borderColor: `${colorScheme}.200`,
        }}
        onClick={() => handleBusinessClick(business)}
      >
        {/* Rank Section */}
        <Flex
          direction='column'
          align='center'
          justify='center'
          minW='40px'
          mr={3}
        >
          <Text
            fontSize='xl'
            fontWeight='bold'
            color={isTop3 ? rankColors[position] : rankColor}
            lineHeight='1'
          >
            {position + 1}.
          </Text>
          {isTop3 && (
            <Icon
              as={rankIcons[position]}
              color={rankColors[position]}
              boxSize={3.5}
              mt={1}
            />
          )}
        </Flex>

        {/* Content Section */}
        <Box flex='1'>
          <Flex justify='space-between' align='center' mb={1}>
            <HStack spacing={2} maxW='70%'>
              <Text
                fontWeight='bold'
                fontSize='md'
                color={textColor}
                noOfLines={1}
              >
                {business.name}
              </Text>
              <IconButton
                icon={
                  <Icon
                    as={isFavorited(business.maps_uri) ? FaStar : FaRegStar}
                  />
                }
                size='xs'
                variant='ghost'
                colorScheme={isFavorited(business.maps_uri) ? 'yellow' : 'gray'}
                onClick={(e) => handleFavoriteClick(e, business.maps_uri)}
                aria-label='Toggle favorite'
                minW='auto'
                h='auto'
                p={1}
                _hover={{ color: 'yellow.400' }}
              />
            </HStack>
            <Text
              fontSize='lg'
              fontWeight='bold'
              color={`${colorScheme}.500`}
            >
              +{metric.toLocaleString()}
              {unit}
            </Text>
          </Flex>

          <Progress
            value={percentage}
            size='xs'
            colorScheme={colorScheme}
            borderRadius='full'
            bg={`${colorScheme}.50`}
            sx={{
              '& > div': {
                transition: 'width 1s ease-in-out',
              },
            }}
          />
        </Box>
      </Flex>
    );
  };

  if (!yearData) {
    return null; // Don't render anything if data isn't ready
  }

  if (
    !lastMonthMetrics.businesses ||
    lastMonthMetrics.businesses.length === 0
  ) {
    return (
      <Box
        bg={cardBg}
        borderRadius='lg'
        p={8}
        mb={8}
        borderWidth='1px'
        borderColor={borderColor}
      >
        <Text textAlign='center' color={subtitleColor}>
          No data available for {lastMonthDisplay}
        </Text>
      </Box>
    );
  }

  return (
    <Box mb={8}>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Growth in Reviews Column */}
        <Box>
          <HStack mb={4} spacing={2} align='center'>
            <Icon as={FaChartLine} color='blue.500' boxSize={5} />
            <Heading size='md' color={textColor} textTransform="uppercase" fontStyle="italic" fontSize="1.3em">
              Growth in Reviews
            </Heading>
            <Text fontSize='sm' color={subtitleColor} ml={2}>
               in {lastMonthDisplay}
            </Text>
          </HStack>
          
          <Box>
            {topByVolume.map((business, idx) => (
              <LeaderRow
                key={business.maps_uri || idx}
                business={business}
                position={idx}
                metric={business.lastMonthReviews}
                unit=''
                colorScheme='blue'
                maxMetric={topByVolume[0]?.lastMonthReviews || 1}
              />
            ))}
          </Box>
        </Box>

        {/* Percent Acceleration Column */}
        <Box>
          <HStack mb={4} spacing={2} align='center'>
            <Icon as={FaFire} color='green.500' boxSize={5} />
            <Heading size='md' color={textColor} textTransform="uppercase" fontStyle="italic" fontSize="1.3em">
              Percent Acceleration
            </Heading>
             <Text fontSize='sm' color={subtitleColor} ml={2}>
               in {lastMonthDisplay}
            </Text>
          </HStack>

          <Box>
            {topByGrowth.map((business, idx) => (
              <LeaderRow
                key={business.maps_uri || idx}
                business={business}
                position={idx}
                metric={parseFloat(business.relativeGrowth.toFixed(1))}
                unit='%'
                colorScheme='green'
                maxMetric={topByGrowth[0]?.relativeGrowth || 1}
              />
            ))}
          </Box>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default LastMonthLeaders;
